import { useNavigate } from 'react-router-dom';
import { PageOuterContainer } from '../../../../root-components/layouts/page-outer-container';
import { PageHeading } from '../../../presentation-components/display-elements-components/page-heading';
import { IconName } from '../../../../shared/interfaces/icon-picker.model';
import { useContext, useEffect, useState } from 'react';
import { AddConversionSourceDialog } from '../../../dialogs/super-admin/add-conversion-source-dialog';
import { IConversionSource, IPostConversionSource } from '../../../../shared/services/conversion-source/conversion-source.model';
import ConversionSourceService from '../../../../shared/services/conversion-source/conversion-source.service';
import { IHeadCell } from '../../../../shared/interfaces/table-display.model';
import { TableActionButtons } from '../../../presentation-components/miscellaneous-components/table-action-buttons';
import { Chip, Stack } from '@mui/material';
import { TableDisplay } from '../../../presentation-components/miscellaneous-components/table-display';
import { LoadingPage } from '../../loading-page';
import { UserNotificationsContext } from '../../../../context/user-notifications/user-notifications';

export const SuperAdminConversionFactorsPage = () => {
  const navigate = useNavigate();
  //Services
  const conversionSourceService = new ConversionSourceService();
  //Context
  const userNotificationsContext = useContext(UserNotificationsContext);
  //State
  const [conversionSources, setConversionSources] = useState<IConversionSource[]>([]);
  const [isAddConversionSourceDialogOpen, setIsAddConversionSourceDialogOpen] = useState<boolean>(false);
  const [selectedConversionSourceId, setSelectedConversionSourceId] = useState<string | null>(null);
  //Table State
  const [selected, setSelected] = useState<string[]>([]);
  const [inputPage, setInputPage] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [change, setChange] = useState<number>(0);
  const [orderBy, setOrderBy] = useState<string>();
  const [order, setOrder] = useState<'asc' | 'desc'>();
  //Loading State
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [hasComponentMounted, setHasComponentMounted] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(true)

  useEffect(() => {
    getConversionSources();
  }, [inputPage, perPage, change, orderBy, order]);

  const getConversionSources = () => {
    conversionSourceService.getAllWithPagination(inputPage, perPage, orderBy, order, 'includeMostRecentYear=true')
      .then(response => {
        setIsLoading(false);
        setConversionSources(response.rows);
        setTotalRecords(response.count);
      })
      .catch(error => {
        setIsLoading(false);
        console.error(error);
      });
  }

  useEffect(() => {
    if (hasComponentMounted) {
      if (inputPage === 0) {
        setChange(change + 1)
      } else {
        setInputPage(0)
      }
    }
    setHasComponentMounted(true)
  }, [order, orderBy])


  const handleEdit = (id: string) => {
    setSelectedConversionSourceId(id);
    setIsAddConversionSourceDialogOpen(true);
  }

  const submitHandler = (values: IPostConversionSource, resetForm: () => void, isEdit: boolean, conversionSourceId?: string) => {
    if (isEdit && conversionSourceId) {
      setIsSubmitting(true);
      conversionSourceService.update(conversionSourceId, values)
        .then(response => {
          console.log(response);
          setIsSubmitting(false);
          resetForm();
          setIsAddConversionSourceDialogOpen(false);
          setSelectedConversionSourceId(null);
          getConversionSources();
          userNotificationsContext.openSnackbar({ open: true, message: 'Updated successfully' });
        })
        .catch(err => {
          console.log(err);
          userNotificationsContext.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: 'text.primary' });
          setIsSubmitting(false);
        });
    } else {
      setIsSubmitting(true);
      conversionSourceService.create(values)
        .then(response => {
          userNotificationsContext.openSnackbar({ open: true, message: 'Saved successfully' });
          setIsSubmitting(false);
          resetForm();
          setIsAddConversionSourceDialogOpen(false);
          setSelectedConversionSourceId(null);
          getConversionSources();
        })
        .catch(err => {
          console.log(err);
          userNotificationsContext.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: 'text.primary' });
          setIsSubmitting(false);
        });
    }
  }

  //Table Contents
  const headCells: IHeadCell[] = [
    { id: 'actions', numeric: false, label: 'Actions', sortable: false },
    { id: 'source', numeric: false, label: 'Source', sortable: true, isHeading: true },
    { id: 'conversionType', numeric: false, label: 'Conversion Type', sortable: true },
    { id: 'country', numeric: false, label: 'Country', sortable: true },
    { id: 'year', numeric: false, label: 'Most Recent Year', sortable: true },
    { id: 'active', numeric: false, label: 'Status', sortable: true },

  ]

  const rows = conversionSources.map(conversionSource => {
    return {
      id: conversionSource.id,
      source: conversionSource.source,
      conversionType: conversionSource.conversionType === 'spendBasedConversion' ? 'Spend Based Conversion Factors' : conversionSource.conversionType === 'squareFootConversion' ? 'Square Foot Conversion Factors' : 'General Conversion Factors',
      country: conversionSource.country ? (
        <Stack display='flex' flexDirection='row' alignItems='center' gap={1}>
          <img
            loading="lazy"
            width="20"
            src={conversionSource.country.imageUrl}
            alt={`${conversionSource.country.name} flag`}
          />
          {conversionSource.country.name}
        </Stack>
      ) : null,
      year: conversionSource.mostRecentActiveYear ? conversionSource.mostRecentActiveYear : 'N/A',
      active: conversionSource.active ? <Chip color='success' label='Active' variant='outlined' sx={{ width: '80px' }} /> : <Chip color='error' label='Inactive' variant='outlined' sx={{ width: '80px' }} />,
      actions: (
        <TableActionButtons
          includeEdit={true}
          includeView={true}
          includeDeactivate={conversionSource.active ? true : false}
          includeOther={!conversionSource.active && !!conversionSource.mostRecentActiveYear ? true : false}
          otherButtonIcon={IconName.Autorenew}
          otherButtonText={'Activate'}
          onOther={() => handleActivate(conversionSource.id)}
          onDeactivate={() => handleDeactivate(conversionSource.id)}
          onView={() => navigate(`/super-admin/conversion-factors/${conversionSource.id}`)}
          onEdit={() => handleEdit ? handleEdit(conversionSource.id) : undefined}
          id={conversionSource.id}
        />
      )
    }
  });

  const handleActivate = (id: string) => {
    conversionSourceService.activate(id)
      .then(response => {
        userNotificationsContext.openSnackbar({ open: true, message: 'Updated successfully' });
        getConversionSources();
      })
      .catch(err => {
        userNotificationsContext.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: 'text.primary' });
        console.log(err);
      });
  }

  const handleDeactivate = (id: string) => {
    conversionSourceService.deactivate(id)
      .then(response => {
        userNotificationsContext.openSnackbar({ open: true, message: 'Updated successfully' });
        getConversionSources();
      })
      .catch(err => {
        userNotificationsContext.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: 'text.primary' });
        console.log(err);
      });
  }


  const handleOrderChange = (newOrderBy: string, newOrder: 'asc' | 'desc') => {
    setOrderBy(newOrderBy);
    setOrder(newOrder);
  };

  return (
    <PageOuterContainer>
      <PageHeading
        title='Manage Conversion Factors'
        includeBackButton={true}
        backButtonLink='/super-admin'
        backButtonText='Back to super admin'
        includeActionButton={true}
        actionButtonIcon={IconName.Add}
        actionButtonText='Add Conversion Source'
        actionButtonHandler={() => setIsAddConversionSourceDialogOpen(true)}
      />
      <LoadingPage pageReady={!isLoading}>
        <TableDisplay
          onOrderChange={handleOrderChange}
          headCells={headCells}
          rows={rows}
          selected={selected}
          setSelected={setSelected}
          selectByIcon={true}
          onRowClick={(id) => navigate(`/super-admin/conversion-factors/${id}`)}
          inputPage={inputPage}
          setInputPage={setInputPage}
          totalRecords={totalRecords}
          perPage={perPage}
          setPerPage={setPerPage}
        />
      </LoadingPage>
      {isAddConversionSourceDialogOpen &&
        <AddConversionSourceDialog
          open={isAddConversionSourceDialogOpen}
          closeHandler={() => setIsAddConversionSourceDialogOpen(false)}
          isEdit={false}
          isSubmitting={isSubmitting}
          onSubmit={submitHandler}
        />}
      {isAddConversionSourceDialogOpen && selectedConversionSourceId &&
        <AddConversionSourceDialog
          open={isAddConversionSourceDialogOpen}
          closeHandler={() => setIsAddConversionSourceDialogOpen(false)}
          isEdit={true}
          conversionSourceId={selectedConversionSourceId}
          isSubmitting={isSubmitting}
          onSubmit={submitHandler}
        />}
    </PageOuterContainer>
  );
}

